import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Loader2, Sparkles, X, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const LS_SESSION = "grafton_qualify_session_id";

interface Msg {
  role: "user" | "assistant";
  content: string;
}

const QUICK_PROMPTS = [
  "Je dirige une PME, comment Grafton m'aide ?",
  "Quels sont les tarifs ?",
  "Qu'est-ce que vous faites de mieux que HubSpot ?",
  "C'est pour quelle taille d'entreprise ?",
];

export default function LandingChat() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try { return localStorage.getItem(LS_SESSION); } catch { return null; }
  });
  const [suggestSignup, setSuggestSignup] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load history if a session already exists
  useEffect(() => {
    if (!sessionId || !open) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/chat/qualify/history?session_id=${encodeURIComponent(sessionId)}`);
        if (!r.ok) return;
        const d = await r.json();
        const msgs: Msg[] = (d.messages || []).map((m: { role: string; content: string }) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
        }));
        setMessages(msgs);
      } catch { /* tolerate */ }
    })();
  }, [sessionId, open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isLoading]);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setIsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/chat/qualify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      const newSid = d.session_id;
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        try { localStorage.setItem(LS_SESSION, newSid); } catch { /* ignore */ }
      }
      setMessages((prev) => [...prev, { role: "assistant", content: d.reply || "" }]);
      if (d.suggest_signup) setSuggestSignup(true);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Je reviens dans 2 secondes, reformule la question." }]);
    }
    setIsLoading(false);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  return createPortal(
    <>
      {!open && (
        <button
          data-testid="landing-chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] h-14 pr-5 pl-4 rounded-full bg-primary text-primary-foreground shadow-[0_4px_24px_rgba(200,150,50,0.3)] hover:shadow-[0_4px_32px_rgba(200,150,50,0.5)] flex items-center gap-2 transition-all duration-200 hover:scale-105 active:scale-95"
          aria-label="Discuter avec Grafton"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="text-sm font-medium">Discute avec Grafton</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[9998] bg-black/50 transition-opacity" onClick={() => setOpen(false)} />
      )}

      <div
        data-testid="landing-chat-panel"
        className={`fixed bottom-4 right-4 z-[9999] w-[92vw] sm:w-[400px] h-[calc(100vh-2rem)] max-h-[640px] bg-background border border-border rounded-2xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${
          open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between bg-primary/5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Grafton IA</p>
              <p className="text-[10px] text-muted-foreground">On voit si on matche en 2 minutes</p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Salut, je suis Grafton</p>
                <p className="text-xs text-muted-foreground mt-1">Dis-moi ce que tu fais et ton défi n°1 — je te dis si on peut t'aider.</p>
              </div>
              <div className="grid grid-cols-1 gap-1.5 w-full max-w-[300px]">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="text-xs text-left px-3 py-2 rounded-lg bg-secondary/50 border border-border/50 hover:border-primary/30 hover:bg-secondary transition-colors"
                    data-testid={`landing-chat-prompt-${p.slice(0, 12)}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="h-6 w-6 rounded-md bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="h-3 w-3 text-primary" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/60 border border-border/40"
                }`}>
                  {m.content}
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex gap-2">
              <div className="h-6 w-6 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
                <Sparkles className="h-3 w-3 text-primary" />
              </div>
              <div className="bg-secondary/60 border border-border/40 rounded-xl px-3 py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>

        {suggestSignup && (
          <div className="px-3 py-2 border-t border-border/40 bg-primary/5" data-testid="landing-chat-signup-cta">
            <Button
              className="w-full h-9 gap-1.5"
              onClick={() => navigate("/inscription")}
              data-testid="landing-chat-signup-btn"
            >
              Crée ton compte gratuit <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        <div className="p-3 border-t border-border/50">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              data-testid="landing-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Tape ta question..."
              rows={1}
              className="flex-1 resize-none bg-secondary/50 border border-border/50 rounded-xl px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/40 transition-colors"
              style={{ maxHeight: "100px" }}
            />
            <Button
              data-testid="landing-chat-send-btn"
              size="icon"
              className="h-9 w-9 rounded-xl shrink-0"
              onClick={() => send(input)}
              disabled={!input.trim() || isLoading}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
